'use client'

type Props = {
  value: number
  /** Units available for the selected dates (the range availability `remaining`). */
  max: number
  onChange: (n: number) => void
  /** Singular noun for one unit (e.g. "board"). Pluralized with a trailing s. */
  noun?: string
}

/**
 * −/+ quantity stepper capped at real availability. The customer can never
 * request more units than exist for the chosen dates — overbooking would split
 * the deposit from the rental charge, the same money bug fixed in the back
 * office. Only rendered when capacity allows ≥2 (see BookingForm).
 */
export function QtyStepper({ value, max, onChange, noun = 'board' }: Props) {
  const dec = () => onChange(Math.max(1, value - 1))
  const inc = () => onChange(Math.min(max, value + 1))
  const btn =
    'min-h-[44px] min-w-[44px] rounded-md border border-[var(--color-border)] text-lg ' +
    'text-[var(--color-text-primary)] disabled:opacity-40 disabled:cursor-not-allowed ' +
    'hover:bg-[var(--color-border)]/40 transition-colors'

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[var(--color-text-secondary)]">
        {noun.charAt(0).toUpperCase() + noun.slice(1)}s
        <span className="text-[var(--color-text-muted)]"> · {max} available</span>
      </span>
      <div className="flex items-center gap-3">
        <button type="button" aria-label={`Fewer ${noun}s`} onClick={dec} disabled={value <= 1} className={btn}>−</button>
        <span aria-live="polite" className="w-6 text-center font-medium text-[var(--color-text-primary)]">{value}</span>
        <button type="button" aria-label={`More ${noun}s`} onClick={inc} disabled={value >= max} className={btn}>+</button>
      </div>
    </div>
  )
}
